/**
 * The site-wide admin REST surface. Every call carries the session cookie;
 * the server answers 401 signed out and 403 for an account that is not an
 * admin.
 */
import type {
  AdminDay,
  AdminMarketFlow,
  AdminMarketMover,
  BalanceSeries,
  GameType,
  MemberStatsView,
  RoundEntry,
  StatsWindow,
} from '@tv-casino/shared';

export type { AdminDay, AdminMarketFlow, AdminMarketMover };

export interface AdminGameRow {
  gameType: GameType;
  hands: number;
  players: number;
  wagered: number;
  net: number;
  lastPlayedAt: string | null;
}

export interface AdminMarketDay {
  day: string;
  issued: number;
  wagered: number;
  playersNet: number;
  supply: number;
}

export interface AdminMarketSector {
  gameType: GameType;
  hands: number;
  wagered: number;
  returned: number;
  playersNet: number;
  best: number;
  worst: number;
}

export interface AdminMarketPrint {
  id: string;
  casinoName: string;
  memberName: string;
  gameType: GameType;
  outcome: string;
  stake: number;
  net: number;
  at: string;
}

export interface AdminMarket {
  generatedAt: string;
  supply: { chips: number; issued: number; playersNet: number; holders: number; drift: number };
  breadth: { up: number; down: number; flat: number };
  flows: AdminMarketFlow[];
  daily: AdminMarketDay[];
  sectors: AdminMarketSector[];
  gainers: AdminMarketMover[];
  losers: AdminMarketMover[];
  active: AdminMarketMover[];
  prints: { wins: AdminMarketPrint[]; losses: AdminMarketPrint[] };
}

export interface AdminOverview {
  generatedAt: string;
  users: { total: number; new7d: number; admins: number };
  casinos: { total: number; new7d: number };
  players: { total: number; active24h: number; active7d: number };
  rounds: { total: number; last24h: number; last7d: number };
  hands: { total: number; wagered: number; net: number };
  live: { rooms: number; playersConnected: number; tvs: number };
  games: AdminGameRow[];
  daily: AdminDay[];
}

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  playerName: string | null;
  createdAt: string;
  lastActiveAt: string | null;
  casinos: number;
  isAdmin: boolean;
  bakedAdmin: boolean;
}

export interface AdminCasinoRow {
  id: string;
  name: string;
  joinCode: string;
  ownerEmail: string;
  createdAt: string;
  tables: number;
  liveTables: number;
  players: number;
  hands: number;
  wagered: number;
  lastPlayedAt: string | null;
}

export interface AdminPlayerRow {
  id: string;
  casinoId: string;
  casinoName: string;
  name: string;
  chips: number;
  host: boolean;
  createdAt: string;
  lastSeenAt: string;
  hands: number;
  wagered: number;
  net: number;
  games: GameType[];
  lastPlayedAt: string | null;
}

export interface AdminRoundRow extends RoundEntry {
  casinoId: string;
  casinoName: string;
  memberName: string;
}

export interface AdminRoundsResponse {
  entries: AdminRoundRow[];
  hasMore: boolean;
}

export interface AdminLiveRoom {
  code: string;
  gameType: GameType;
  phase: string;
  round: number;
  casinoId: string | null;
  casinoName: string | null;
  tableId: string | null;
  players: { name: string; chips: number; connected: boolean }[];
  tv: boolean;
  createdAt: string;
}

export interface AdminRoundQuery {
  casinoId?: string;
  memberId?: string;
  gameType?: string;
  limit?: number;
  offset?: number;
}

const failure = async (res: Response, fallback: string): Promise<Error> => {
  try {
    const body = (await res.json()) as { message?: string };
    return new Error(body.message ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

async function get<T>(path: string, fallback: string): Promise<T> {
  const res = await fetch(`/api/admin${path}`, { credentials: 'include' });
  if (!res.ok) throw await failure(res, fallback);
  return (await res.json()) as T;
}

/** False for a signed-out tab or an ordinary account; never throws. */
export async function fetchIsAdmin(): Promise<boolean> {
  try {
    const res = await fetch('/api/admin/me', { credentials: 'include' });
    if (!res.ok) return false;
    return ((await res.json()) as { isAdmin?: boolean }).isAdmin === true;
  } catch {
    return false;
  }
}

export const fetchAdminOverview = () => get<AdminOverview>('/overview', 'Could not read the overview.');
export const fetchAdminMarket = (window: StatsWindow = 'all') =>
  get<AdminMarket>(`/market?window=${window}`, 'Could not read the market.');
export const fetchAdminUsers =() => get<AdminUserRow[]>('/users', 'Could not read the users.');
export const fetchAdminCasinos = () => get<AdminCasinoRow[]>('/casinos', 'Could not read the casinos.');
export const fetchAdminPlayers = () => get<AdminPlayerRow[]>('/players', 'Could not read the players.');
export const fetchAdminLive = () => get<AdminLiveRoom[]>('/live', 'Could not read the live rooms.');

export function fetchAdminRounds(query: AdminRoundQuery = {}): Promise<AdminRoundsResponse> {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') p.set(key, String(value));
  }
  const qs = p.toString();
  return get(`/rounds${qs ? `?${qs}` : ''}`, 'Could not read the rounds.');
}

/** Promote or demote an account. The server refuses the baked admin and self-demotion. */
export async function setAdmin(userId: string, isAdmin: boolean): Promise<AdminUserRow> {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ isAdmin }),
  });
  if (!res.ok) throw await failure(res, 'Could not change admin access.');
  return (await res.json()) as AdminUserRow;
}
